import { useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Modal } from '@/components/Modal';
import { PollModal } from '@/components/PollModal';
import { isPollDismissed, useClosedPollResults, usePendingPolls, type PollView } from '@/lib/polls/usePolls';

/**
 * 홈에 들어온 사람 앞에 서는 투표 창 (사용자 지시 2026-09-02).
 *
 * <h2>한 번에 하나만</h2>
 * 투표가 둘이면 둘 다 띄우지 않고 <b>먼저 연 것부터</b> 하나만 세운다. 창을 닫자마자 다음 창이
 * 서면 사람은 읽지 않고 닫는다 — 그렇게 받은 표는 물어본 값이 아니다.
 *
 * <h2>스스로 서는 것은 «묻는 창» 하나뿐이다 (사용자 지적 2026-09-02)</h2>
 * 전에는 결과도 저절로 떴다 — 마감된 투표에 참여했으면 홈에 들어오는 순간 결과 창이 섰다.
 * 그래서 <b>이미 투표한 사람에게도 창이 계속 떴다</b>: "투표했는데 또 나오더니, 결과가 나오네".
 *
 * <p>물어볼 것이 있을 때만 막아선다. 결과는 알려야 할 것이지 <b>가로막아야 할 것이 아니다</b> —
 * TO DO 할일의 「‥ 투표 결과가 나왔습니다」 줄로 남고, 누른 사람에게만 열린다.
 *
 * <h2>홈에서만 뜬다</h2>
 * 다른 화면에서도 띄우면 <b>일하는 중에 끼어드는 창</b>이 된다. 홈은 들어오는 자리라, 여기서
 * 묻는 것은 일을 끊지 않는다.
 *
 * <h2>할일 줄이 이 창의 유일한 다른 입구다</h2>
 * TO DO 위젯의 두 줄이 모두 {@code /home?poll=<id>} 로 걸려 있다({@code TodoService.pollTodos}) —
 * 아직 안 한 투표는 <b>미룬 표시를 무시하고</b> 물음 창을, 마감된 투표는 결과 창을 연다.
 * 사이드바 「위젯 관리」 가 {@code ?widgets=1} 로 홈을 여는 것과 같은 방식이다.
 */
export function PollPrompt() {
  const pending = usePendingPolls();
  const results = useClosedPollResults();
  const [searchParams, setSearchParams] = useSearchParams();

  /* 미룬 표시는 리액트 밖(세션 저장소)에 산다 — 서버에 남기지 않기로 했기 때문이다. 그래서 창을
     닫아도 이 컴포넌트는 다시 그려질 이유가 없다. 닫힌 것이 화면에서 사라지도록 한 번 민다. */
  const [, redraw] = useReducer((tick: number) => tick + 1, 0);

  /* 새로고침·뒤로가기로 같은 창이 다시 열리지 않도록 표시를 지운다(HomePage 의 `widgets` 와 같다). */
  const clearRequest = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('poll');
        return next;
      },
      { replace: true },
    );
  };

  /* 지목된 것은 미뤘든 아니든 연다 — 할일 줄을 누른 사람은 「지금 보겠다」 고 말한 것이다.
     아직 안 한 투표면 물음 창이, 마감된 투표면 결과 창이 열린다. */
  const requestedId = searchParams.get('poll');
  const requestedAsk = requestedId ? (pending.data ?? []).find((poll) => poll.id === requestedId) : undefined;
  const requestedResult = requestedId ? (results.data ?? []).find((poll) => poll.id === requestedId) : undefined;
  const loading = pending.isPending || results.isPending;

  // 어느 쪽에도 없으면(이미 했는데 아직 안 끝났다) 표시만 지우고 조용히 빠진다 — 「없는
  // 투표입니다」 를 띄워 봐야 누른 사람이 할 수 있는 일이 없다. 그리는 도중에 주소를 고칠 수는
  // 없으므로 여기서.
  useEffect(() => {
    if (requestedId && !requestedAsk && !requestedResult && !loading) clearRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedId, requestedAsk, requestedResult, loading]);

  if (requestedAsk) return <PollModal poll={requestedAsk} onClose={clearRequest} />;
  if (requestedResult) return <PollResultModal poll={requestedResult} onClose={clearRequest} />;

  /*
    저절로 서는 것은 여기 하나뿐이다 — <b>아직 안 한 투표</b>. 던졌거나 미룬 것은 이 목록에서
    빠지고({@code dismissPoll}), 마감된 결과는 애초에 스스로 서지 않는다.
   */
  const asking = (pending.data ?? []).find((poll) => !isPollDismissed(poll.id));
  if (asking) {
    return <PollModal poll={asking} onClose={redraw} />;
  }

  return null;
}

/**
 * 끝난 투표의 결과.
 *
 * <p><b>스스로 서지 않는다.</b> TO DO 할일의 「‥ 투표 결과가 나왔습니다」 줄을 누른 사람에게만
 * 열린다 — 결과는 알려야 할 것이지 가로막아야 할 것이 아니다.
 */
function PollResultModal({ poll, onClose }: { poll: PollView; onClose: () => void }) {
  /* 직접 적은 표도 던져진 표다 — 막대의 분모에 넣는다. 준비된 선택지만으로 100% 를 만들면
     「일곱 명 중 넷」 이 「다섯 명 중 넷」 으로 보인다. */
  const written = poll.writeIns.reduce((sum, row) => sum + row.voteCount, 0);
  const total = poll.options.reduce((sum, option) => sum + (option.voteCount ?? 0), 0) + written;
  const top = Math.max(...poll.options.map((option) => option.voteCount ?? 0), 0);

  return (
    <Modal
      title={poll.title}
      maxWidth="480px"
      onClose={onClose}
      footer={
        <button onClick={onClose} className="btn-primary">
          확인
        </button>
      }
    >
      {/* 물었던 이유는 결과 옆에도 남는다 — 무엇을 위해 고른 것인지 모르는 결과는 숫자일 뿐이다. */}
      {poll.description && (
        <p className="text-ui-sm" style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
          {poll.description}
        </p>
      )}

      <div className="space-y-2">
        {poll.options.map((option) => {
          const count = option.voteCount ?? 0;
          return (
            <div key={option.id} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="text-ui-sm"
                  style={{
                    color: 'var(--text-primary)',
                    // 1등만 굵다. 여럿이 같은 표면 같이 굵어진다 — 임의로 하나를 고르지 않는다.
                    fontWeight: count === top && count > 0 ? 700 : 400,
                  }}
                >
                  {option.label}
                </span>
                <span className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
                  {count}표
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 999, background: 'var(--border)' }}>
                <div
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background: 'var(--accent)',
                    width: total > 0 ? `${(count / total) * 100}%` : 0,
                  }}
                />
              </div>

              {/* 그 칸을 고르며 덧붙인 말들(V353). 막대를 붙이지 않는다 — 표는 이미 위 막대에
                  세어져 있고, 이것은 그 표가 무엇을 뜻하는지 풀어 준 글이다. */}
              {option.notes.length > 0 && (
                <ul className="space-y-0.5 pt-0.5">
                  {option.notes.map((text, index) => (
                    <li key={index} className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                      · {text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/*
        직접 적은 안은 <b>묶어서</b> 온다(같은 문구끼리). 막대를 붙이지 않는 이유는 이것들이
        서로 겨룬 것이 아니기 때문이다 — 하나씩 나온 안 열 개를 막대로 그리면 「아무도 안 골랐다」
        처럼 보인다. 여기서는 무엇이 나왔는지가 먼저다.
      */}
      {poll.writeIns.length > 0 && (
        <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
          {poll.writeIns.map((row) => (
            <div key={row.text} className="flex items-baseline justify-between gap-2">
              <span className="text-ui-sm" style={{ color: 'var(--text-primary)' }}>
                {row.text}
              </span>
              <span className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
                {row.voteCount}표
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
        {poll.participantCount}명 참여
      </p>
    </Modal>
  );
}
